/* eslint-disable no-unused-vars */
import BarProgress from "@components/Progress/BarProgress";
import Footer from "@components/layout/Footer";
import { auth } from "@utils/firebase";
import { useGetUser, useGetUserBookings } from "@utils/hooks";
import DashboardFactFind from "@view/dashboard/DashboardFactFind";
import DashboardPennySpace from "@view/dashboard/DashboardPennySpace";
import DashboardPensionApplied from "@view/dashboard/DashboardPensionApplied";
import DashboardPensionFinal from "@view/dashboard/DashboardPensionFinal";
import DashboardProgressEmployerDetails from "@view/dashboard/DashboardProgressEmployerDetails";
import DashboardRecommedations from "@view/dashboard/DashboardRecommedations";
import DashboardWaitingEmployerDetails from "@view/dashboard/DashboardWaitingEmployerDetails";
import WelcomePenny from "@view/dashboard/WelcomePenny";
import DashboardBookCall from "@view/dashboard/subcomponents/DashboardBookCall";
import React, { useEffect, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { BookingData, UserData } from "../../view/dashboard/subcomponents/DashboardBookCall";

type DashboardProps = {};

const Dashboard: React.FC<DashboardProps> = () => {
  const { data: dataUserBooking } = useGetUserBookings();

  const [factFindCompleted, setFactFindCompleted] = useState(false);
  const [factFindPercentage, setFactFindPercentage] = useState(0);
  const [nextFactFindStep, setNextFactFindStep] = useState(
    "/fact-find/personal-details"
  );
  const { data: userData, isLoading: userDataLoading } = useGetUser();
  const [userAuth] = useAuthState(auth);

  const [step, setStep] = useState(0);

  useEffect(() => {
    const hasRecommendations =
      userData?.recommend_zurich_standard ||
      userData?.recommend_zurich_nonstandard;
    const hasPensionApplied = userData?.pension_applied;

    setStep(
      [
        factFindCompleted,
        dataUserBooking || userData?.process_employer_details === "waiting",
        hasRecommendations,
        hasPensionApplied,
      ].filter((item) => !!item).length
    );
  }, [factFindCompleted, dataUserBooking, userData]);

  useEffect(() => {
    const factFindSteps = [
      userData?.form_fact_find_financial_details,
      userData?.form_fact_find_personal_details,
      userData?.form_fact_find_risk_reward,
    ].map((i) => (i === true ? 1 : 0));

    //percentage is the arithmetic average of steps
    const factFindPercentage =
      factFindSteps.reduce((a: number, b: number) => a + b, 0) /
      factFindSteps.length;

    if (factFindPercentage === 1) {
      setFactFindCompleted(true);
    }

    if (!userData?.form_fact_find_personal_details) {
      setNextFactFindStep("/fact-find/personal-details");
    } else if (!userData?.form_fact_find_financial_details) {
      setNextFactFindStep("/fact-find/financial-details");
    } else if (!userData?.form_fact_find_risk_reward) {
      setNextFactFindStep("/fact-find/risk-reward");
    }
    setFactFindPercentage(parseInt(String(factFindPercentage * 100)));
  }, [
    userData?.form_fact_find_financial_details,
    userData?.form_fact_find_personal_details,
    userData?.form_fact_find_risk_reward,
  ]);

  const getRecommendations = useMemo(() => {
    if (!userDataLoading) {
      const recommendations = [];

      if (userData?.recommend_zurich_standard) {
        recommendations.push("recommend_zurich_standard");
      }

      if (userData?.recommend_zurich_nonstandard) {
        recommendations.push("recommend_zurich_nonstandard");
      }

      return recommendations;
    }
  }, [userData, userDataLoading]);

  const activeStep = useMemo(() => {
    if (userData?.process_employer_details === "waiting") {
      return 2;
    }

    if (userData?.pension_applied) {
      return 4;
    }

    if (dataUserBooking) {
      return 3;
    }

    if (getRecommendations && getRecommendations?.length > 0) {
      return 1;
    }

    return 0;
  }, [getRecommendations, dataUserBooking, userData]);

  useEffect(() => {
    console.log('Debug DashboardPensionApplied conditions:', {
      pension_applied: userData?.pension_applied,
      pension_applied_type: typeof userData?.pension_applied,
      pension_applied_value: userData?.pension_applied,
      process_employer_details: userData?.process_employer_details,
      dataUserBooking: !!dataUserBooking,
      process_final_pension_application: userData?.process_final_pension_application,
      final_pension_application: userData?.final_pension_application,
      step: step,
      factFindPercentage: factFindPercentage,
      factFindCompleted: factFindCompleted,
      hasRecommendations: getRecommendations ? getRecommendations.length > 0 : false
    });
  }, [userData, dataUserBooking, step, factFindPercentage, factFindCompleted, getRecommendations]);

  // Add debug logging for render path
  const renderPath = useMemo(() => {
    if (userData?.process_final_pension_application) return 'path1';
    if (userData?.pension_applied) {
      if (userData?.process_employer_details === "progress") return 'path3';
      if (userData?.process_employer_details === "done") return 'path4';
      return 'path5'; // Should show DashboardPensionApplied
    }
    if (userData?.process_employer_details === "progress") return 'path6';
    return 'path7';
  }, [userData]);

  useEffect(() => {
    console.log('Current render path:', renderPath);
  }, [renderPath]);

  return (
    <section className="w-full">
      {userData?.process_final_pension_application ? (
        <>
          {console.log('Rendering path1 - DashboardPensionFinal')}
          <DashboardPensionFinal data={userData} />
        </>
      ) : userData?.pension_applied ? (
        <>
          {console.log('Rendering pension_applied branch')}
          {userData?.process_employer_details === "progress" ? (
            <>
              {console.log('Rendering path3 - DashboardProgressEmployerDetails progress')}
              <DashboardProgressEmployerDetails progress={"progress"} />
            </>
          ) : userData?.process_employer_details === "done" ? (
            <>
              {console.log('Rendering path4 - DashboardProgressEmployerDetails done')}
              <DashboardProgressEmployerDetails progress={"done"} />
            </>
          ) : (
            <>
              {console.log('Rendering path5 - DashboardPensionApplied with optional booking')}
              <div className="flex flex-col gap-y-4">
                <DashboardPensionApplied data={userData} />
                {dataUserBooking && (
                  <>
                    {/* <div className="p-4">
                      <BarProgress
                        label="Get Penny Ready"
                        step={step}
                        finalStep={4}
                        labelClassesLeft="text-pn-body-emphasis"
                        labelClassesRight="text-pn-body-regular"
                      />
                    </div> */}
                    <DashboardBookCall
                      data_booking={dataUserBooking as BookingData | null}
                      userData={userData as UserData | null}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </>
      ) : userData?.process_employer_details === "progress" ? (
        <>
          {console.log('Rendering path6 - DashboardProgressEmployerDetails progress')}
          <DashboardProgressEmployerDetails progress={"progress"} />
        </>
      ) : (
        <>
          {console.log('Rendering path7 - Default view')}
          {factFindPercentage < 1 && <WelcomePenny />}

          {dataUserBooking || userData?.process_employer_details === "waiting" ? (
            dataUserBooking ? (
              <DashboardBookCall
                data_booking={dataUserBooking as BookingData | null}
                userData={userData as UserData | null}
              />
            ) : (
              <div className="max-w-screen-lg mx-auto">
                <DashboardWaitingEmployerDetails userData={userData} />
              </div>
            )
          ) : getRecommendations && getRecommendations?.length < 1 ? (
            <DashboardFactFind
              userData={{ userData: userData as UserData | null, userAuth }}
              questionnaireData={{
                fact_find_completed: factFindCompleted,
                fact_find_percentage: factFindPercentage,
                next_fact_find_step: nextFactFindStep,
              }}
            />
          ) : (
            userData !== undefined && userData !== null ? (
              <DashboardRecommedations userData={{ userData, userAuth }} />
            ) : null
          )}
        </>
      )}

      <DashboardPennySpace
        pension_values={{
          retirement: Number(userData?.retirement_income_target_value) ?? 0,
          contribution: Number(userData?.pension_contribution_value) ?? 0,
        }}
      />

      <Footer />
    </section>
  );
};
export default Dashboard;
